/**
 * Every basemap and camera knob the map has, in one place.
 *
 * The point of this module is that swapping the basemap is a one-file change.
 * Today the tiles come from OpenFreeMap - no key, no quota, no sign-up. When
 * the project self-hosts a PMTiles planet extract, the only edit is the two
 * URLs below (or the two environment variables that override them); no
 * component imports a style URL.
 */

import type { ColorScheme } from '../theme/palette'

/**
 * OpenFreeMap's hosted styles. `liberty` is the OSM-Bright-derived default;
 * `dark` is its sibling, used when the visitor prefers a dark colour scheme.
 * Both are served from the same host, share the same glyph and sprite
 * endpoints, and carry OpenFreeMap / OpenMapTiles / OpenStreetMap attribution
 * in their TileJSON.
 */
export const OPENFREEMAP_STYLES = {
  light: 'https://tiles.openfreemap.org/styles/liberty',
  dark: 'https://tiles.openfreemap.org/styles/dark',
} as const satisfies Record<ColorScheme, string>

export interface MapEnv {
  readonly VITE_MAP_STYLE_URL?: string | undefined
  readonly VITE_MAP_STYLE_URL_DARK?: string | undefined
  readonly [key: string]: unknown
}

const trimmed = (value: string | undefined): string | undefined => {
  const candidate = value?.trim()
  return candidate === undefined || candidate === '' ? undefined : candidate
}

/**
 * The style URL for one colour scheme.
 *
 * `VITE_MAP_STYLE_URL` overrides the light style, `VITE_MAP_STYLE_URL_DARK` the
 * dark one. Setting only the first pins both schemes to that style, which is
 * what a single self-hosted PMTiles style wants.
 */
export const resolveBasemapStyleUrl = (
  scheme: ColorScheme,
  env: MapEnv = import.meta.env,
): string => {
  const light = trimmed(env.VITE_MAP_STYLE_URL)
  if (scheme === 'light') return light ?? OPENFREEMAP_STYLES.light
  return trimmed(env.VITE_MAP_STYLE_URL_DARK) ?? light ?? OPENFREEMAP_STYLES.dark
}

/**
 * The attribution the basemap does not supply.
 *
 * The style's TileJSON already declares OpenFreeMap, OpenMapTiles and
 * OpenStreetMap; what it cannot know is where the charger data came from, which
 * the UDT's reuse conditions require to be stated. The text itself comes from
 * `i18n/strings.ts` like every other string, and is handed to the map at
 * creation - this only records that the obligation exists.
 */
export type DataAttribution = string

/**
 * Poland, with a little air around it. Deliberately a touch wider than the
 * administrative border so the coastline and the mountains are not flush
 * against the viewport edge.
 */
export const POLAND_BOUNDS: [[number, number], [number, number]] = [
  [14.07, 48.98],
  [24.16, 54.92],
]

export const MAP_VIEW = {
  bounds: POLAND_BOUNDS,
  /** Padding, in px, when framing {@link POLAND_BOUNDS}. */
  fitPadding: 24,
  minZoom: 4,
  maxZoom: 18,
  /** Zoom used when easing to a selected site: past {@link CLUSTERING.maxZoom}, so it is a single dot. */
  selectionZoom: 14,
  /** Fallback step when a cluster cannot report its expansion zoom. */
  clusterZoomStep: 1.5,
  /** Camera animation length. Zero when the visitor prefers reduced motion. */
  easeDurationMs: 650,
} as const

/**
 * The opening view once the visitor's position is known.
 *
 * The frame is built around the visitor rather than around the chargers: it is
 * symmetric on the fix, so the blue dot lands in the middle of the screen, and
 * it grows until the nearest {@link LOCATION_VIEW.minSites} sites are inside it.
 * Both halves matter - a frame that only held the dots would push the dot to an
 * edge, and a fixed radius would show an empty map in the countryside and a
 * single street in a city centre.
 *
 * `minHalfSpanDeg` is the floor, for a visitor standing in a car park with five
 * chargers in it; `maxZoom` is the ceiling the same case runs into first, and
 * either on its own would leave the other case looking wrong.
 */
export const LOCATION_VIEW = {
  /** How many of the nearest sites the frame has to contain. */
  minSites: 5,
  /** Never frame tighter than this, in degrees of latitude - roughly 2.2 km. */
  minHalfSpanDeg: 0.02,
  /** Never zoom past this, however close the nearest few happen to be. */
  maxZoom: 14,
  /** Padding, in px, when framing the location view. */
  fitPadding: 48,
} as const

export const CLUSTERING = {
  /** Cluster radius in px at each zoom level. Roughly two dot diameters. */
  radius: 56,
  /** Above this zoom every site is drawn on its own. */
  maxZoom: 12,
  /** A pair of sites stays a pair; three is where a cluster starts paying off. */
  minPoints: 3,
} as const

/**
 * How many sites the keyboard list offers at once. The list is a linear scan
 * for keyboard and screen-reader users, not a substitute for the filter panel,
 * so it is capped rather than virtualised.
 */
export const KEYBOARD_LIST_LIMIT = 40
